import { useState, useRef } from "react";
import type { AppSettings } from "@/lib/queries";
import { useAiModels } from "@/lib/queries";
import { useUpdateAppSettings, useAiTest, useTranscribeTest } from "@/lib/mutations";
import { getModelSettings } from "@emr/shared";
import { ModelPicker, CUSTOM_MODEL } from "./ModelPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Sparkles, FlaskConical, Thermometer, Mic, Bot } from "lucide-react";
import { toast } from "sonner";

// Full reasoning-effort set accepted by the OpenRouter gateway. Used when a
// model has a reasoning object but no supported_efforts (meaning all gateway
// efforts are accepted) and for custom slugs (model unknown).
const FULL_EFFORTS = ["max", "xhigh", "high", "medium", "low", "minimal", "none"];
const EFFORT_LABELS: Record<string, string> = {
  max: "Max",
  xhigh: "Extra High",
  high: "High",
  medium: "Medium",
  low: "Low",
  minimal: "Minimal",
  none: "None",
};
// Radix Select forbids empty-string item values — sentinel for "Default".
const REASONING_DEFAULT = "__default__";

// Dictation upload formats (0020) — the mic button encodes its recording
// into the picked format before uploading to /ai/transcribe. WAV is the
// default: the only format verified against every OpenRouter STT model
// including Azure-backed MAI-Transcribe 2.
const TRANSCRIBE_FORMATS: { value: string; label: string; hint: string }[] = [
  { value: "wav", label: "WAV", hint: "Works with all STT models (recommended)" },
  { value: "flac", label: "FLAC", hint: "Lossless, ~50% smaller than WAV" },
  { value: "mp3", label: "MP3", hint: "Lossy, small, widely supported" },
  { value: "aac", label: "AAC", hint: "Native browser recording (mp4) — Chrome/Edge/Safari" },
  { value: "webm", label: "WebM", hint: "Smallest — rejected by Azure-backed models (MAI-Transcribe 2)" },
];

export function AiCard({ settings, canEdit }: { settings: AppSettings; canEdit: boolean }) {
  const [apiKey, setApiKey] = useState(settings.aiApiKey === "***" ? "" : settings.aiApiKey ?? "");
  const [endpoint, setEndpoint] = useState(settings.aiEndpoint);
  const [model, setModel] = useState(settings.aiModel);
  const [temperature, setTemperature] = useState(settings.aiTemperature ?? 0.7);
  // Last non-null slider value — restored when switching from a custom slug
  // back to a known model (the DB value is null while a custom slug is saved).
  const lastKnownTempRef = useRef<number>(settings.aiTemperature ?? 0.7);
  const [testResult, setTestResult] = useState<{ ok: boolean; message?: string; model?: string } | null>(null);
  const [transcribeModel, setTranscribeModel] = useState(settings.aiTranscribeModel);
  const [transcribeStyle, setTranscribeStyle] = useState(settings.aiTranscribeStyle);
  // Dictation upload format (0020) — falls back to "wav" when the saved
  // settings predate the column (undefined from an older API response).
  const [transcribeFormat, setTranscribeFormat] = useState(settings.aiTranscribeFormat || "wav");
  const [transcribeTestResult, setTranscribeTestResult] = useState<{ ok: boolean; message?: string; model?: string } | null>(null);
  // ---- Agentic Flows (0030, 2026-10-18; moved here from the AI Chat tab by
  // 0031 the same day) — separate model + temperature for the agentic AI
  // feature (the apply wizard's suggest-assignments AI; the standalone
  // schedule-chat assistant was removed 2026-10-18). Empty model = fall
  // back to the text model; empty temperature = send none (model default).
  // Never borrows the text model's temperature. ----
  const [agenticModel, setAgenticModel] = useState(settings.aiAgenticModel ?? "");
  const [agenticTemp, setAgenticTemp] = useState(settings.aiAgenticTemperature?.toString() ?? "");
  const update = useUpdateAppSettings();
  const testMut = useAiTest();
  const transcribeTestMut = useTranscribeTest();

  // Live model list — refetched on every Settings page open (staleTime 0).
  const { data: modelsData, isLoading: modelsLoading } = useAiModels();
  const textModels = modelsData?.text ?? [];
  const transcribeModels = modelsData?.transcription ?? [];

  // Explicit picker choices. null = "follow the saved slug" — the effective
  // value is DERIVED on every render so it updates when the live list arrives
  // (the query starts empty; a useState initializer would run before the fetch
  // resolves and wrongly pin the picker to "Custom"). Once the user picks an
  // option, the explicit choice sticks. Saved slug in list → known model;
  // otherwise custom (free-text slug input).
  const [textModelSelection, setTextModelSelection] = useState<string | null>(null);
  const [transcribeModelSelection, setTranscribeModelSelection] = useState<string | null>(null);
  // Explicit reasoning-effort choice. null = derive from the saved value.
  const [reasoningSelection, setReasoningSelection] = useState<string | null>(null);

  // Custom-slug detection. While the live list is loading we can't know —
  // treat the saved slug as known so the controls don't flash disabled and a
  // premature save doesn't null out the temperature. Once the list has loaded
  // (even empty — OpenRouter unreachable), a slug missing from the list is
  // custom: the backend then sends neither temperature nor reasoning, and the
  // model's own defaults apply.
  const textIsCustom =
    textModelSelection === CUSTOM_MODEL ||
    (!modelsLoading && !textModels.some((m) => m.id === model));
  const transcribeIsCustom =
    transcribeModelSelection === CUSTOM_MODEL ||
    (!modelsLoading && !transcribeModels.some((m) => m.id === transcribeModel));

  // Reasoning metadata for the selected (known) text model. Custom slugs have
  // no metadata — the reasoning dropdown renders disabled with the full set.
  const selectedTextModel = textModels.find((m) => m.id === model) ?? null;
  const reasoningMeta = selectedTextModel?.reasoning ?? null;
  const showReasoning = textIsCustom || reasoningMeta !== null;
  const effortOptions = reasoningMeta?.supportedEfforts ?? FULL_EFFORTS;
  // "" = Default (model's own) — sends no reasoning parameter.
  const effectiveReasoning =
    reasoningSelection !== null
      ? reasoningSelection
      : settings.aiReasoningEffort && effortOptions.includes(settings.aiReasoningEffort)
        ? settings.aiReasoningEffort
        : "";

  // Save + Test the AI key only. Sends aiEndpoint + aiModel + aiApiKey so the key is
  // testable against the right endpoint/model in the same round-trip. Never touches
  // temperature. If the key field is blank and a key is already stored, sends "***"
  // (server treats "***" as "keep existing") so the key is not cleared.
  const saveAndTestKey = async () => {
    setTestResult(null);
    const patch: Record<string, unknown> = {};
    if (endpoint !== settings.aiEndpoint) patch.aiEndpoint = endpoint;
    if (model !== settings.aiModel) patch.aiModel = model;
    // Custom slug → the backend must send neither temperature nor reasoning.
    // saveModelSettings handles that on its own save; this save path also
    // touches aiModel, so keep the DB consistent here too.
    if (textIsCustom && settings.aiTemperature !== null) patch.aiTemperature = null;
    if (textIsCustom && settings.aiReasoningEffort !== null) patch.aiReasoningEffort = null;
    if (apiKey !== "") {
      patch.aiApiKey = apiKey;
    } else if (settings.aiApiKey && settings.aiApiKey !== "") {
      patch.aiApiKey = "***";
    }
    if (Object.keys(patch).length > 0) {
      try {
        await update.mutateAsync(patch);
      } catch { return; }
    } else {
      toast.message("No key changes to save");
    }
    try {
      // Test the DISPLAYED model, not the saved one.
      const res = await testMut.mutateAsync(model);
      setTestResult(res);
    } catch (e) {
      const err = e as Error & { body?: { message?: string } };
      setTestResult({ ok: false, message: err?.body?.message ?? err.message });
    }
  };

  // Save model/endpoint/temperature/reasoning only. Does NOT touch the AI key
  // (sends "***" if a key exists so the server keeps it, or omits it if no key
  // exists). Does NOT test. Custom slug → temperature AND reasoning are saved
  // as null (the backend sends neither — model defaults apply).
  // Returns false when the save FAILED (caller early-outs); true when saved
  // or there was nothing to save (both proceed to a follow-up test).
  const saveModelSettings = async (): Promise<boolean> => {
    const patch: Record<string, unknown> = {};
    if (endpoint !== settings.aiEndpoint) patch.aiEndpoint = endpoint;
    if (model !== settings.aiModel) patch.aiModel = model;
    const tempToSave = textIsCustom ? null : temperature;
    if (tempToSave !== settings.aiTemperature) patch.aiTemperature = tempToSave;
    const effortToSave = textIsCustom || effectiveReasoning === "" ? null : effectiveReasoning;
    if (effortToSave !== settings.aiReasoningEffort) patch.aiReasoningEffort = effortToSave;
    if (Object.keys(patch).length === 0) {
      toast.message("No model settings to save");
      return true;
    }
    try {
      await update.mutateAsync(patch);
      return true;
    } catch {
      return false; // onError shows the toast
    }
  };

  // Save model settings, then test the DISPLAYED model (mirrors section 3's
  // Save & Test). Early-returns if the save fails so the test doesn't run
  // against a stale/unsaved state.
  const saveAndTestModel = async () => {
    const saved = await saveModelSettings();
    if (!saved) return;
    try {
      const res = await testMut.mutateAsync(model);
      setTestResult(res);
    } catch (e) {
      const err = e as Error & { body?: { message?: string } };
      setTestResult({ ok: false, message: err?.body?.message ?? err.message });
    }
  };

  const testOnly = async () => {
    setTestResult(null);
    try {
      // Test the DISPLAYED model, not the saved one.
      const res = await testMut.mutateAsync(model);
      setTestResult(res);
    } catch (e) {
      const err = e as Error & { body?: { message?: string } };
      setTestResult({ ok: false, message: err?.body?.message ?? err.message });
    }
  };

  // Save transcription model + style, then test. Does NOT touch the AI key.
  // The saved aiTranscribeStyle stays in the DB even for custom slugs — the
  // backend already scopes provider options to microsoft/mai-transcribe-2 only.
  const saveAndTestTranscribe = async () => {
    setTranscribeTestResult(null);
    const patch: Record<string, unknown> = {};
    if (transcribeModel !== settings.aiTranscribeModel) patch.aiTranscribeModel = transcribeModel;
    if (transcribeStyle !== settings.aiTranscribeStyle) patch.aiTranscribeStyle = transcribeStyle;
    if (transcribeFormat !== (settings.aiTranscribeFormat || "wav")) patch.aiTranscribeFormat = transcribeFormat;
    if (Object.keys(patch).length > 0) {
      try {
        await update.mutateAsync(patch);
      } catch { return; }
    } else {
      toast.message("No transcription changes to save");
    }
    try {
      // Test the DISPLAYED transcription model, not the saved one.
      const res = await transcribeTestMut.mutateAsync(transcribeModel);
      setTranscribeTestResult(res);
    } catch (e) {
      const err = e as Error & { body?: { message?: string } };
      setTranscribeTestResult({ ok: false, message: err?.body?.message ?? err.message });
    }
  };

  const testTranscribeOnly = async () => {
    setTranscribeTestResult(null);
    try {
      // Test the DISPLAYED transcription model, not the saved one.
      const res = await transcribeTestMut.mutateAsync(transcribeModel);
      setTranscribeTestResult(res);
    } catch (e) {
      const err = e as Error & { body?: { message?: string } };
      setTranscribeTestResult({ ok: false, message: err?.body?.message ?? err.message });
    }
  };

  // Save the Agentic Flows pair only. Empty model = not set (null = fall
  // back to the text model); empty temperature = not set (null = send NO
  // temperature, model default). Invalid numbers are rejected by the
  // backend (0-2); parse defensively and bail with a toast instead.
  const saveAgentic = async () => {
    const modelToSave = agenticModel.trim() || null;
    const tempTrimmed = agenticTemp.trim();
    let tempToSave: number | null = null;
    if (tempTrimmed !== "") {
      const n = Number(tempTrimmed);
      if (!Number.isFinite(n) || n < 0 || n > 2) {
        toast.error("Agentic temperature must be a number between 0 and 2");
        return;
      }
      tempToSave = n;
    }
    try {
      await update.mutateAsync({ aiAgenticModel: modelToSave, aiAgenticTemperature: tempToSave });
    } catch { /* toast shown */ }
  };

  const onTextModelChange = (v: string) => {
    setTextModelSelection(v);
    if (v !== CUSTOM_MODEL) {
      setModel(v);
      // Leaving custom mode: restore the pre-custom slider value and
      // re-derive the reasoning effort for the newly selected model.
      setTemperature(lastKnownTempRef.current);
      setReasoningSelection(null);
    }
  };

  const onTranscribeModelChange = (v: string) => {
    setTranscribeModelSelection(v);
    if (v !== CUSTOM_MODEL) setTranscribeModel(v);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500 flex items-center gap-2"><Sparkles size={16} /> OpenRouter (AI)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Section 1: API Key + Endpoint — saving here also tests the key. */}
        <div className="space-y-3">
          <div>
            <Label>Endpoint</Label>
            <Input value={endpoint} disabled={!canEdit} onChange={(e) => setEndpoint(e.target.value)} />
          </div>
          <div>
            <Label>API Key {settings.aiApiKey === "***" && <span className="text-xs text-muted ml-2">(currently set — leave blank to keep)</span>}</Label>
            <Input type="password" value={apiKey} disabled={!canEdit} onChange={(e) => setApiKey(e.target.value)} placeholder={settings.aiApiKey === "***" ? "••••••••" : "sk-or-v1-..."} />
          </div>
          {testResult && (
            <Alert variant={testResult.ok ? "default" : "destructive"}>
              <AlertDescription>{testResult.ok ? `AI test succeeded — key is working${testResult.model ? ` (model: ${testResult.model})` : ""}.` : `AI test failed: ${testResult.message ?? "unknown error"}`}</AlertDescription>
            </Alert>
          )}
          {canEdit && (
            <div className="flex gap-2">
              <Button onClick={saveAndTestKey} disabled={update.isPending || testMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                <Save size={14} className="mr-1" /> Save key & Test
              </Button>
              <Button variant="outline" onClick={testOnly} disabled={testMut.isPending}>
                <FlaskConical size={14} className="mr-1" /> Test only
              </Button>
            </div>
          )}
        </div>

        {/* Section 2: Text Model — searchable picker with pricing, temperature
            + reasoning effort. Saving here never touches the AI key. For a
            CUSTOM slug (not in the live list) the temperature and reasoning
            controls are disabled and saved as null — the backend sends
            neither, so the model's own defaults apply. */}
        <div className="space-y-3 border-t border-cream-200 pt-4 dark:border-dark-700">
          <div className="flex items-center gap-2">
            <Thermometer size={16} className="text-maroon-700 dark:text-amber-500" />
            <span className="text-sm font-medium">Text Model</span>
          </div>
          <div>
            <Label>Model</Label>
            <ModelPicker
              models={textModels}
              value={model}
              onChange={onTextModelChange}
              isTranscription={false}
              disabled={!canEdit}
              loading={modelsLoading}
              message={modelsData?.message}
            />
            {textIsCustom && (
              <Input value={model} disabled={!canEdit} onChange={(e) => setModel(e.target.value)} placeholder="org/model-name" className="mt-2" />
            )}
            {modelsLoading && <p className="text-xs text-muted mt-1">Loading models…</p>}
            {modelsData?.message && !textModels.length && (
              <p className="text-xs text-muted mt-1">{modelsData.message}</p>
            )}
          </div>
          <div>
            <Label>Temperature: {temperature.toFixed(1)}</Label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              disabled={!canEdit || textIsCustom}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setTemperature(v);
                lastKnownTempRef.current = v;
              }}
              className="w-full"
            />
            {textIsCustom && (
              <p className="text-xs text-muted mt-1">Model defaults apply — temperature is not sent for custom slugs.</p>
            )}
          </div>
          {showReasoning && (
            <div>
              <Label>Reasoning effort</Label>
              <Select
                value={effectiveReasoning === "" ? REASONING_DEFAULT : effectiveReasoning}
                onValueChange={(v) => setReasoningSelection(v === REASONING_DEFAULT ? "" : v)}
                disabled={!canEdit || textIsCustom}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={REASONING_DEFAULT}>Default (model's own)</SelectItem>
                  {effortOptions.map((e) => (
                    <SelectItem key={e} value={e}>{EFFORT_LABELS[e] ?? e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted">
                {textIsCustom
                  ? "Model defaults apply — reasoning is not sent for custom slugs."
                  : reasoningMeta?.defaultEffort
                    ? `Default sends no reasoning parameter — the model's own effort (${EFFORT_LABELS[reasoningMeta.defaultEffort] ?? reasoningMeta.defaultEffort}) applies.`
                    : "Default sends no reasoning parameter — the model's own effort applies."}
              </p>
            </div>
          )}
          {canEdit && (
            <div className="flex gap-2">
              <Button onClick={saveAndTestModel} disabled={update.isPending || testMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                <Save size={14} className="mr-1" /> Save & Test
              </Button>
              <Button variant="outline" onClick={testOnly} disabled={testMut.isPending}>
                <FlaskConical size={14} className="mr-1" /> Test only
              </Button>
            </div>
          )}
        </div>

        {/* Section 3: Transcription Model — searchable picker with pricing +
            conditional per-model settings (e.g. transcribeStyle for
            MAI-Transcribe 2). For a CUSTOM slug the conditional setting is
            disabled — the backend already ignores provider options for
            non-MAI models. */}
        <div className="space-y-3 border-t border-cream-200 pt-4 dark:border-dark-700">
          <div className="flex items-center gap-2">
            <Mic size={16} className="text-maroon-700 dark:text-amber-500" />
            <span className="text-sm font-medium">Transcription Model</span>
          </div>
          <div>
            <Label>Model</Label>
            <ModelPicker
              models={transcribeModels}
              value={transcribeModel}
              onChange={onTranscribeModelChange}
              isTranscription
              disabled={!canEdit}
              loading={modelsLoading}
              message={modelsData?.message}
            />
            {transcribeIsCustom && (
              <Input value={transcribeModel} disabled={!canEdit} onChange={(e) => setTranscribeModel(e.target.value)} placeholder="org/model-name" className="mt-2" />
            )}
            {modelsLoading && <p className="text-xs text-muted mt-1">Loading models…</p>}
            {modelsData?.message && !transcribeModels.length && (
              <p className="text-xs text-muted mt-1">{modelsData.message}</p>
            )}
          </div>

          {/* Conditional settings — from the getModelSettings() static map in
              @emr/shared (provider-specific options the models API doesn't
              expose). For custom slugs the map has no entry, so the
              transcribeStyle setting (the only one today) renders DISABLED
              with a hint; the saved value stays in the DB but is unused. */}
          {transcribeIsCustom
            ? getModelSettings("microsoft/mai-transcribe-2").map((setting) => (
                <div key={setting.key}>
                  <Label>{setting.label}</Label>
                  <Select
                    value={transcribeOptionsValue(setting.key, setting.defaultValue, transcribeStyle)}
                    onValueChange={setTranscribeStyle}
                    disabled
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {setting.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted">Provider options unavailable for custom slugs</p>
                </div>
              ))
            : getModelSettings(transcribeModel).map((setting) => (
                <div key={setting.key}>
                  <Label>{setting.label}</Label>
                  <Select
                    value={transcribeOptionsValue(setting.key, setting.defaultValue, transcribeStyle)}
                    onValueChange={(v) => setTranscribeStyle(v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {setting.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {setting.description && (
                    <p className="mt-1 text-xs text-muted">{setting.description}</p>
                  )}
                </div>
              ))}

          {/* Dictation upload format (0020) — independent of the model (works
              for custom slugs too); the mic button encodes its recording
              into this format before uploading. Saved via the same
              Save & Test patch as the transcription model. */}
          <div>
            <Label>Dictation upload format</Label>
            <Select
              value={transcribeFormat}
              onValueChange={setTranscribeFormat}
              disabled={!canEdit}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRANSCRIBE_FORMATS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted">
              {TRANSCRIBE_FORMATS.find((f) => f.value === transcribeFormat)?.hint}
            </p>
            <p className="mt-1 text-xs text-muted">
              The Test buttons test with WAV; verify other formats via dictation.
            </p>
          </div>

          {transcribeTestResult && (
            <Alert variant={transcribeTestResult.ok ? "default" : "destructive"}>
              <AlertDescription>{transcribeTestResult.ok ? `Transcription test succeeded${transcribeTestResult.model ? ` (model: ${transcribeTestResult.model})` : ""}.` : `Transcription test failed: ${transcribeTestResult.message ?? "unknown error"}`}</AlertDescription>
            </Alert>
          )}
          {canEdit && (
            <div className="flex gap-2">
              <Button onClick={saveAndTestTranscribe} disabled={update.isPending || transcribeTestMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                <Save size={14} className="mr-1" /> Save & Test
              </Button>
              <Button variant="outline" onClick={testTranscribeOnly} disabled={transcribeTestMut.isPending}>
                <FlaskConical size={14} className="mr-1" /> Test only
              </Button>
            </div>
          )}
        </div>

        {/* Section 4: Agentic Flows (0030, 2026-10-18; moved here from the
            admin-only AI Chat tab by 0031 the same day) — separate model +
            temperature for the agentic AI feature (the apply wizard's
            suggest-assignments AI; the standalone schedule-chat assistant
            was removed 2026-10-18). Empty model = fall back to the text
            model (above); empty temperature = send none (model default).
            Never borrows the text model's temperature. Gated by the same
            settings.ai canEdit as every other field in this card. */}
        <div className="space-y-3 border-t border-cream-200 pt-4 dark:border-dark-700">
          <div className="flex items-center gap-2">
            <Bot size={16} className="text-maroon-700 dark:text-amber-500" />
            <span className="text-sm font-medium">Agentic Flows</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="agentic-model">Agentic model</Label>
              <Input
                id="agentic-model"
                value={agenticModel}
                disabled={!canEdit}
                onChange={(e) => setAgenticModel(e.target.value)}
                placeholder="org/model-name"
              />
              <p className="text-xs text-muted">
                Effective: <span className="font-medium">{agenticModel.trim() || settings.aiModel}</span>
                {!agenticModel.trim() && " (falls back to the text model)"}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agentic-temp">Agentic temperature</Label>
              <Input
                id="agentic-temp"
                type="number"
                step={0.1}
                min={0}
                max={2}
                value={agenticTemp}
                disabled={!canEdit}
                onChange={(e) => setAgenticTemp(e.target.value)}
                placeholder="model default"
              />
              <p className="text-xs text-muted">Empty = model default (no temperature sent).</p>
            </div>
          </div>
          <p className="text-xs text-muted">
            Used by the apply wizard AI. Leave empty to fall back to the text model.
          </p>
          {canEdit && (
            <Button onClick={saveAgentic} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              <Save size={14} className="mr-1" /> Save agentic settings
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// The conditional-settings Select is bound to a single settings field per key.
// Today the only conditional setting is transcribeStyle (key "transcribeStyle"),
// stored in aiTranscribeStyle. If a future model adds another key, extend this
// mapping (or generalize to a Record<string, string> state).
function transcribeOptionsValue(key: string, defaultValue: string, transcribeStyle: string): string {
  if (key === "transcribeStyle") return transcribeStyle || defaultValue;
  return defaultValue;
}
